import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { InvoiceRunType } from '@/graphql/generatedTypes';
import { cn } from '@/lib/utils';

import { useCompleteAndChargeInvoiceRunMutation } from './CompleteAndChargeInvoiceRun.generated';
import {
  formatCount,
  normalizePeriodInput,
  periodLabel,
  runTypeLabel,
  shortRunId,
} from './invoiceRunPresentation';

/** The fields the modal needs from the generation run it charges. */
export type ChargeModalRun = {
  id: string;
  type: InvoiceRunType;
  year: number;
  iso_month: number;
  invoice_count?: number | null;
  error_count?: number | null;
  open_error_count: number;
};

type ChargeModalProps = {
  run: ChargeModalRun | null;
  onOpenChange: (open: boolean) => void;
  onCharged: () => void;
};

/**
 * The highest-stakes action in the admin: completes every invoice of the run
 * in Billomat, sets up SEPA and emails partners — all-or-nothing, not
 * undoable. The type-the-period friction keeps it deliberate.
 */
export function ChargeModal({
  run,
  onOpenChange,
  onCharged,
}: ChargeModalProps) {
  const [confirmation, setConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [chargeRun] = useCompleteAndChargeInvoiceRunMutation();

  useEffect(() => {
    if (run) setConfirmation('');
  }, [run]);

  const period = useMemo(
    () =>
      run
        ? periodLabel(run.type === InvoiceRunType.Es, run.year, run.iso_month)
        : '',
    [run],
  );

  if (!run) return null;

  // Only invoice_count are invoices; processed_count also counts every store
  // the run checked and billed nothing. Null on runs from before the column.
  const invoiceCount = run.invoice_count;
  const matches =
    normalizePeriodInput(confirmation) === normalizePeriodInput(period);

  const errorSummary =
    !run.error_count || run.error_count === 0
      ? 'None'
      : run.open_error_count === 0
        ? 'All resolved'
        : `${formatCount(run.open_error_count)} open error${
            run.open_error_count === 1 ? '' : 's'
          }`;

  const handleCharge = async () => {
    setSubmitting(true);
    try {
      await chargeRun({ variables: { generation_run_id: run.id } });
      toast.success('Charge run started');
      onCharged();
      onOpenChange(false);
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : 'Starting the charge run failed',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="tw:sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Complete &amp; charge {period}?</DialogTitle>
          <DialogDescription>
            This completes{' '}
            {invoiceCount != null
              ? `${formatCount(invoiceCount)} invoices`
              : 'every invoice of this run'}{' '}
            in Billomat, sets up their SEPA payments and emails them to
            partners. It applies to the whole run — there is no per-invoice
            selection — and it cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="tw:flex tw:flex-col tw:gap-4">
          <div className="tw:overflow-hidden tw:rounded-lg tw:border tw:border-border">
            {[
              [
                'Generation run',
                <span key="run" className="tw:font-mono">
                  {shortRunId(run.id)}
                  <span className="tw:font-sans">
                    {' '}
                    · {runTypeLabel(run.type)}
                  </span>
                </span>,
              ],
              // The exact completion count is only known at charge time; the
              // run's own created count is the honest number we have.
              [
                'Invoices created in this run',
                invoiceCount != null ? formatCount(invoiceCount) : '—',
              ],
              [
                'Stores with errors',
                <span
                  key="errors"
                  className={cn(
                    run.open_error_count > 0 &&
                      'tw:font-semibold tw:text-destructive',
                  )}
                >
                  {errorSummary}
                </span>,
              ],
              ['Emails to partners', 'Sent immediately'],
            ].map(([label, value]) => (
              <div
                key={typeof label === 'string' ? label : 'row'}
                className="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:border-b tw:border-border tw:px-3 tw:py-2 tw:text-sm tw:last:border-b-0"
              >
                <span className="tw:text-muted-foreground">{label}</span>
                <span className="tw:text-right tw:font-medium tw:tabular-nums">
                  {value}
                </span>
              </div>
            ))}
          </div>
          <div className="tw:flex tw:flex-col tw:gap-2">
            <p className="tw:text-sm tw:text-muted-foreground">
              Type{' '}
              <span className="tw:rounded tw:bg-muted tw:px-1.5 tw:py-0.5 tw:font-mono tw:text-foreground">
                {period}
              </span>{' '}
              to confirm
            </p>
            <Input
              autoFocus
              className="tw:font-mono"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              placeholder={period}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button disabled={!matches || submitting} onClick={handleCharge}>
            Complete &amp; charge
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
